// Section Map — interactive SVG plan-view of a track section (your line vs ideal,
// brake/throttle points, corner phases, hover scrub). Vanilla port of the React
// reference (chat-section-widget/source/section-map.jsx). Series colours are the
// fixed Apex telemetry visual language — do not theme.

const SM_W = 332,
  SM_H = 212,
  SM_N = 88
const SVG_NS = "http://www.w3.org/2000/svg"

export const SM_C = {
  you: "#A78BFA",
  ideal: "#14F4D9",
  throttle: "#22C55E",
  brake: "#FF5BBE",
  coast: "#7C6BD0",
  loss: "#FF2E92",
  gain: "#22C55E",
  apex: "#FFFFFF",
}

const DEMO_SECTION = {
  title: "Junção → T1",
  mode: "lines",
  annot: "callout",
  paths: {
    center:
      "M 118 204 C 84 197 55 184 57 155 C 59 135 85 130 107 119 C 157 91 211 61 257 39 C 274 30 288 28 296 20",
    ideal:
      "M 113 201 C 81 193 51 179 53 153 C 55 133 81 128 101 116 C 151 87 206 57 252 35 C 270 26 286 24 295 15",
    you: "M 123 206 C 89 199 61 187 63 158 C 65 140 89 134 111 123 C 161 95 215 65 260 43 C 276 34 289 33 297 25",
  },
  points: { thrIdeal: 0.205, thrYou: 0.305, brkIdeal: 0.845, brkYou: 0.745 },
  apex: { jun: 0.15, t1: 0.95 },
  notes: {
    thr: { gap: "0.31s", state: "late", fix: "power earlier" },
    brk: { gap: "9 m", state: "early", fix: "brake later" },
  },
  corners: [
    { label: "Junção", start: 0.05, apex: 0.15, exit: 0.28 },
    { label: "T1", start: 0.87, apex: 0.95, exit: 0.99 },
  ],
  channels: {
    thrYou: (t) => smThrottle(t, 0.305, 0.745),
    thrRef: (t) => smThrottle(t, 0.205, 0.845),
    brkYou: (t) => smBrake(t, 0.745),
    brkRef: (t) => smBrake(t, 0.845),
  },
  speed: (t) => smSpeedAt(t, 0.305, 0.745, 0.9),
  stats: [
    {
      label: "Exit speed",
      value: "112",
      unit: "km/h",
      sub: "−6 vs ideal",
      tone: "loss",
    },
    {
      label: "Top speed",
      value: "274",
      unit: "km/h",
      sub: "−4 vs ideal",
      tone: "loss",
    },
  ],
}

const clamp = (t) => Math.max(0, Math.min(1, t))
const step = (a, b, t) => {
  t = clamp((t - a) / (b - a))
  return t * t * (3 - 2 * t)
}

function smThrottle(t, thrOnT, brkT) {
  if (t > brkT) return 0
  if (t <= 0.14) return Math.max(0, 0.1 - t * 0.5)
  if (t < thrOnT) return step(0.14, thrOnT, t) * 0.45
  return Math.min(1, 0.45 + step(thrOnT, thrOnT + 0.13, t) * 0.55)
}
function smBrake(t, brkT) {
  let b = 0
  if (t < 0.13) b = 1 - step(0, 0.13, t)
  if (t >= brkT) {
    const up = step(brkT, brkT + 0.05, t),
      down = step(0.93, 0.99, t)
    b = Math.max(b, Math.min(up, 1 - down))
  }
  return Math.max(0, Math.min(1, b))
}
function smSpeedAt(t, thrT, brkT, top) {
  const apex = 0.15
  if (t <= thrT)
    return t < apex
      ? 0.44 - step(0, apex, t) * 0.28
      : 0.16 + step(apex, thrT, t) * 0.18
  if (t <= brkT) return 0.34 + step(thrT, brkT, t) * (top - 0.34)
  if (t <= 0.95) return top - step(brkT, 0.95, t) * (top - 0.18)
  return 0.18
}

const SPD_STOPS = [
  [0, [255, 46, 146]],
  [0.42, [255, 122, 28]],
  [0.72, [255, 204, 51]],
  [1, [20, 244, 217]],
]
function spdColor(s) {
  s = clamp(s)
  for (let i = 1; i < SPD_STOPS.length; i++) {
    const [a, ca] = SPD_STOPS[i - 1],
      [b, cb] = SPD_STOPS[i]
    if (s <= b) {
      const f = (s - a) / (b - a)
      return `rgb(${ca.map((v, j) => Math.round(v + (cb[j] - v) * f)).join(",")})`
    }
  }
  return "rgb(20,244,217)"
}
function inputColor(t, points) {
  if (t <= points.thrYou) return SM_C.coast
  if (t <= points.brkYou) return SM_C.throttle
  if (t <= 0.95) return SM_C.brake
  return SM_C.coast
}

function samplePath(d, n) {
  const svg = document.createElementNS(SVG_NS, "svg")
  svg.setAttribute("width", "0")
  svg.setAttribute("height", "0")
  svg.style.cssText =
    "position:absolute;left:-9999px;top:-9999px;opacity:0;pointer-events:none"
  const p = document.createElementNS(SVG_NS, "path")
  p.setAttribute("d", d)
  svg.appendChild(p)
  document.body.appendChild(svg)
  let out = []
  try {
    const L = p.getTotalLength()
    if (L > 0)
      for (let i = 0; i < n; i++) {
        const q = p.getPointAtLength((L * i) / (n - 1))
        out.push([+q.x.toFixed(2), +q.y.toFixed(2)])
      }
  } catch {
    out = []
  }
  document.body.removeChild(svg)
  return out
}
const at = (arr, t) =>
  arr[Math.max(0, Math.min(SM_N - 1, Math.round(t * (SM_N - 1))))]
function normalAt(arr, i) {
  const a = arr[Math.max(0, i - 1)],
    b = arr[Math.min(SM_N - 1, i + 1)]
  const dx = b[0] - a[0],
    dy = b[1] - a[1],
    L = Math.hypot(dx, dy) || 1
  return [-dy / L, dx / L]
}
function sampleChannel(ch, t) {
  if (typeof ch === "function") return clamp(ch(t))
  if (Array.isArray(ch) && ch.length)
    return clamp(ch[Math.round(t * (ch.length - 1))])
  return 0
}
function contextLabel(t, corners) {
  for (const c of corners) {
    if (t >= c.start && t <= c.exit) {
      const ph =
        t < (c.start + c.apex) / 2
          ? "entry"
          : t > (c.apex + c.exit) / 2
            ? "exit"
            : "apex"
      return `${c.label} · ${ph}`
    }
  }
  return "Main straight"
}

function svgEl(tag, attrs = {}) {
  const n = document.createElementNS(SVG_NS, tag)
  for (const [k, v] of Object.entries(attrs)) {
    if (v != null) n.setAttribute(k, v)
  }
  return n
}
function svgText(x, y, str, attrs = {}) {
  const n = svgEl("text", { x, y, ...attrs })
  n.textContent = String(str)
  return n
}

// Escape helper for any user-controlled text inserted as HTML
function esc(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

// Marker key → fixed visual language (colour) + display label. The builder emits
// colour-agnostic notes ({gap,state,fix}); the widget owns colour/label.
const NOTE_META = {
  brk: { key: "brk", label: "Brake", color: SM_C.brake },
  thr: { key: "thr", label: "Throttle", color: SM_C.throttle },
}

// ── Callout auto-placement (pure, DOM-free) ───────────────────────────────
// Pick a tag position for each callout that stays in-bounds and avoids the band,
// corner markers, the other callout, and crossing leader lines. Brute-forced
// over a small candidate set — robust because there are only ≤ 2 labels.
const CALLOUT_TOP = 8,
  CALLOUT_BOT = 24 // block extent above/below the tag baseline
const PLACE_DIRS = [
  [1, 0],
  [0.71, -0.71],
  [0, -1],
  [-0.71, -0.71],
  [-1, 0],
  [-0.71, 0.71],
  [0, 1],
  [0.71, 0.71],
]
const PLACE_RADII = [26, 42]
const W_BAND = 6,
  W_CORNER = 8,
  W_LEN = 0.05,
  W_OVERLAP = 40,
  W_CROSS = 25,
  ABOVE_BONUS = -4

const charW = (s, fs) => s.length * fs * 0.6
// Widest of a callout's three lines, for its bounding box.
function calloutWidth(label, gap, state, fix) {
  return Math.max(
    charW(label.toUpperCase(), 7.5),
    charW(`you ${gap} ${state}`, 9.5),
    charW(`→ ${fix}`, 8),
  )
}
function calloutBBox(tag, w) {
  const x0 = tag.anchor === "end" ? tag.x - w : tag.x
  return {
    minX: x0,
    maxX: x0 + w,
    minY: tag.y - CALLOUT_TOP,
    maxY: tag.y + CALLOUT_BOT,
  }
}
const fitsBox = (b, box) =>
  b.minX >= box.pad &&
  b.maxX <= box.W - box.pad &&
  b.minY >= box.pad &&
  b.maxY <= box.H - box.pad
const ptInBBox = (p, b) =>
  p[0] >= b.minX && p[0] <= b.maxX && p[1] >= b.minY && p[1] <= b.maxY
const bbOverlap = (a, c) =>
  a.minX < c.maxX && a.maxX > c.minX && a.minY < c.maxY && a.maxY > c.minY
function segCross(p1, p2, p3, p4) {
  const d = (a, b, c) =>
    (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])
  const d1 = d(p3, p4, p1),
    d2 = d(p3, p4, p2),
    d3 = d(p1, p2, p3),
    d4 = d(p1, p2, p4)
  return (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
  )
}
function calloutCandidates(item, centerPts, cornerPts, box) {
  const out = []
  for (const [dx, dy] of PLACE_DIRS) {
    for (const r of PLACE_RADII) {
      const tag = {
        x: item.dot[0] + dx * r,
        y: item.dot[1] + dy * r,
        anchor: dx >= 0 ? "start" : "end",
      }
      const bb = calloutBBox(tag, item.w)
      if (!fitsBox(bb, box)) continue
      let score = W_LEN * Math.hypot(tag.x - item.dot[0], tag.y - item.dot[1])
      if (tag.y < item.dot[1]) score += ABOVE_BONUS
      for (const p of centerPts) if (ptInBBox(p, bb)) score += W_BAND
      for (const p of cornerPts) if (ptInBBox(p, bb)) score += W_CORNER
      out.push({ tag, bb, score })
    }
  }
  if (out.length === 0) {
    // degenerate: clamp a near-dot tag into the padded box
    const tag = {
      x: Math.min(Math.max(item.dot[0], box.pad), box.W - box.pad - item.w),
      y: Math.min(
        Math.max(item.dot[1] - 30, box.pad + CALLOUT_TOP),
        box.H - box.pad - CALLOUT_BOT,
      ),
      anchor: "start",
    }
    out.push({ tag, bb: calloutBBox(tag, item.w), score: 1000 })
  }
  return out
}
// items: [{ key, dot:[x,y], w }]. Returns { <key>: {x,y,anchor} }.
export function placeCallouts({ items, centerPts = [], cornerPts = [], box }) {
  const out = {}
  if (!items.length) return out
  const cands = items.map((it) =>
    calloutCandidates(it, centerPts, cornerPts, box),
  )
  if (items.length === 1) {
    out[items[0].key] = cands[0].reduce((a, b) =>
      b.score < a.score ? b : a,
    ).tag
    return out
  }
  let best = null
  for (const a of cands[0]) {
    for (const b of cands[1]) {
      let s = a.score + b.score
      if (bbOverlap(a.bb, b.bb)) s += W_OVERLAP
      if (
        segCross(items[0].dot, [a.tag.x, a.tag.y], items[1].dot, [
          b.tag.x,
          b.tag.y,
        ])
      )
        s += W_CROSS
      if (!best || s < best.s) best = { s, a, b }
    }
  }
  out[items[0].key] = best.a.tag
  out[items[1].key] = best.b.tag
  return out
}

// ── Pan/zoom (pure, DOM-free) ─────────────────────────────────────────────
// Navigate the section by mutating the SVG viewBox: zoom 0.7×–1.3× around the
// fitted view, drag to pan, both bounded so content can't drift away.
const ZOOM_MIN = 0.5,
  ZOOM_MAX = 3.0
const zClamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))
const clampZoom = (z) => zClamp(z, ZOOM_MIN, ZOOM_MAX)

// SVG transform that scales by k about a fixed point c (user-space). Used to
// counter-scale callout labels by 1/zoom so they keep a constant screen size and
// a constant screen offset from their dot, while the leader (anchored at c=dot)
// stays connected at any zoom.
const scaleAbout = (c, k) =>
  `translate(${c[0]} ${c[1]}) scale(${k}) translate(${-c[0]} ${-c[1]})`

// Keep the viewBox bounded loosely: you can pan any part of the section anywhere
// in the view (even leaving empty space, even at default zoom) as long as at
// least PAN_MARGIN of content stays visible on each axis — so it's free but the
// section never fully scrolls off.
const PAN_MARGIN = 40
function clampViewBox([x, y, w, h], box) {
  return [
    zClamp(x, PAN_MARGIN - w, box.W - PAN_MARGIN),
    zClamp(y, PAN_MARGIN - h, box.H - PAN_MARGIN),
    w,
    h,
  ]
}

// viewBox for a zoom factor, keeping `anchor` (an SVG-space point) visually fixed.
function viewBoxForZoom(prev, zoom, anchor, box) {
  const w = box.W / zoom,
    h = box.H / zoom
  const fx = (anchor.x - prev[0]) / prev[2],
    fy = (anchor.y - prev[1]) / prev[3]
  return clampViewBox([anchor.x - fx * w, anchor.y - fy * h, w, h], box)
}

export class SectionMap {
  constructor(section = {}, opts = {}) {
    this.s = mergeSection(section)
    this.opts = opts
    this._hoverI = null
    this._pts = null
    this._callouts = []
    this._scaleGroups = []
    this._dragging = false
    this._onMove = this._onMove.bind(this)
    this._onLeave = this._onLeave.bind(this)
    this._onDown = this._onDown.bind(this)
    this._onUp = this._onUp.bind(this)
    this._onWheel = this._onWheel.bind(this)
    this._onDblClick = this._onDblClick.bind(this)
  }

  mount(container) {
    this.container = container
    this._callouts = []
    this._scaleGroups = []
    this._hoverI = null
    this._zoom = 1
    this._vb = [0, 0, SM_W, SM_H]
    this._dragging = false
    this._build()
    this._pts = {
      you: samplePath(this.s.paths.you, SM_N),
      ideal: samplePath(this.s.paths.ideal, SM_N),
      center: samplePath(this.s.paths.center, SM_N),
    }
    this._drawStatic()
    // Pan/zoom in any mode; the scrub stays lines-mode-only (gated in _onMove).
    this._svg.addEventListener("mousemove", this._onMove)
    this._svg.addEventListener("mouseleave", this._onLeave)
    this._svg.addEventListener("mousedown", this._onDown)
    this._svg.addEventListener("wheel", this._onWheel, { passive: false })
    this._svg.addEventListener("dblclick", this._onDblClick)
    window.addEventListener("mouseup", this._onUp)
    this._renderScrub()
    return this
  }

  destroy() {
    if (this._svg) {
      this._svg.removeEventListener("mousemove", this._onMove)
      this._svg.removeEventListener("mouseleave", this._onLeave)
      this._svg.removeEventListener("mousedown", this._onDown)
      this._svg.removeEventListener("wheel", this._onWheel)
      this._svg.removeEventListener("dblclick", this._onDblClick)
    }
    window.removeEventListener("mouseup", this._onUp)
    if (this.el) this.el.remove()
    this.el = null
    this._svg = null
  }

  // ── private: build the card chrome ─────────────────────────────────────────

  _build() {
    this.container.replaceChildren()

    // Outer card
    const card = document.createElement("div")
    card.className =
      "relative rounded-xl overflow-hidden ring-1 ring-white/[0.07]"
    card.style.background = "#030712"

    // ── Header row (SmFallbackCard header, source 414-418)
    const header = document.createElement("div")
    header.className =
      "flex items-center gap-2 px-3 border-b border-white/[0.06]"
    header.style.height = "32px"

    const titleEl = document.createElement("span")
    titleEl.className =
      "font-mono text-[10px] tracking-[0.16em] uppercase text-white/55 whitespace-nowrap"
    titleEl.textContent = this.s.title
    header.appendChild(titleEl)

    // Legend (right-aligned, SmLegend, source 142-161)
    const legendWrap = document.createElement("span")
    legendWrap.className = "ml-auto shrink-0"
    legendWrap.appendChild(this._buildLegend())
    header.appendChild(legendWrap)

    card.appendChild(header)

    // ── SVG wrapper (relative div, source 280)
    const mapWrap = document.createElement("div")
    mapWrap.className = "relative"

    const svg = document.createElementNS(SVG_NS, "svg")
    svg.setAttribute("viewBox", `0 0 ${SM_W} ${SM_H}`)
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet")
    svg.setAttribute("style", `height:${SM_H}px`)
    svg.classList.add("w-full", "block")
    if (this.s.mode === "lines") svg.classList.add("cursor-crosshair")
    this._svg = svg
    mapWrap.appendChild(svg)

    // HUD overlay (top-1.5 left-1.5, filled by Task 2)
    this._hud = document.createElement("div")
    this._hud.className = "absolute top-1.5 left-1.5 pointer-events-none"
    mapWrap.appendChild(this._hud)

    // Hint overlay (top-2 left-2, filled by Task 2)
    this._hint = document.createElement("div")
    this._hint.className = "absolute top-2 left-2 pointer-events-none"
    mapWrap.appendChild(this._hint)

    // "hover to read inputs" static hint shown when in lines mode before first hover
    if (this.s.mode === "lines") {
      this._hintStatic = document.createElement("div")
      this._hintStatic.className =
        "flex items-center gap-1 font-mono text-[7.5px] text-white/35 pointer-events-none"
      this._hintStatic.innerHTML =
        '<svg class="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>' +
        "<span>hover to read inputs</span>"
      this._hint.appendChild(this._hintStatic)
    }

    card.appendChild(mapWrap)
    this._mapWrap = mapWrap

    this.el = card
    this.container.appendChild(card)
  }

  // Build the legend span (SmLegend, source 142-161)
  _buildLegend() {
    const mode = this.s.mode
    const wrap = document.createElement("span")

    if (mode === "speed") {
      // speed gradient bar
      wrap.className = "flex items-center gap-1.5"

      const slow = document.createElement("span")
      slow.className = "font-mono text-[8.5px] text-white/45"
      slow.textContent = "slow"

      const bar = document.createElement("span")
      bar.className = "h-1.5 w-14 rounded-full"
      bar.style.background =
        "linear-gradient(90deg,#FF2E92,#FF7A1C,#FFCC33,#14F4D9)"

      const fast = document.createElement("span")
      fast.className = "font-mono text-[8.5px] text-white/45"
      fast.textContent = "fast"

      wrap.appendChild(slow)
      wrap.appendChild(bar)
      wrap.appendChild(fast)
    } else if (mode === "inputs") {
      // throttle / brake dots + ideal dashed
      wrap.className = "flex items-center gap-2.5"

      for (const [label, color] of [
        ["throttle", SM_C.throttle],
        ["brake", SM_C.brake],
      ]) {
        const item = document.createElement("span")
        item.className = "flex items-center gap-1"
        const dot = document.createElement("span")
        dot.className = "h-2 w-2 rounded-full"
        dot.style.background = color
        const lbl = document.createElement("span")
        lbl.className = "font-mono text-[8.5px] text-white/55"
        lbl.textContent = label
        item.appendChild(dot)
        item.appendChild(lbl)
        wrap.appendChild(item)
      }

      const idealItem = document.createElement("span")
      idealItem.className = "flex items-center gap-1"
      const idealDash = document.createElement("span")
      idealDash.className = "h-0 w-3 border-t border-dashed"
      idealDash.style.borderColor = SM_C.ideal
      const idealLbl = document.createElement("span")
      idealLbl.className = "font-mono text-[8.5px] text-white/55"
      idealLbl.textContent = "ideal"
      idealItem.appendChild(idealDash)
      idealItem.appendChild(idealLbl)
      wrap.appendChild(idealItem)
    } else {
      // lines mode: you + ideal keys
      wrap.className = "flex items-center gap-2.5"

      const youItem = document.createElement("span")
      youItem.className = "flex items-center gap-1"
      const youLine = document.createElement("span")
      youLine.className = "h-[2px] w-3.5 rounded"
      youLine.style.background = SM_C.you
      const youLbl = document.createElement("span")
      youLbl.className = "font-mono text-[8.5px] text-white/55"
      youLbl.textContent = "you"
      youItem.appendChild(youLine)
      youItem.appendChild(youLbl)
      wrap.appendChild(youItem)

      const idealItem = document.createElement("span")
      idealItem.className = "flex items-center gap-1"
      const idealDash = document.createElement("span")
      idealDash.className = "h-0 w-3.5 border-t border-dashed"
      idealDash.style.borderColor = SM_C.ideal
      const idealLbl = document.createElement("span")
      idealLbl.className = "font-mono text-[8.5px] text-white/55"
      idealLbl.textContent = "ideal"
      idealItem.appendChild(idealDash)
      idealItem.appendChild(idealLbl)
      wrap.appendChild(idealItem)
    }

    return wrap
  }

  // ── private: draw all static SVG content ───────────────────────────────────

  _drawStatic() {
    const svg = this._svg
    const s = this.s
    const pts = this._pts
    const coloured = s.mode === "inputs" || s.mode === "speed"

    // 1. Asphalt band — three centre-path strokes (source 284-287)
    svg.appendChild(
      svgEl("path", {
        d: s.paths.center,
        fill: "none",
        stroke: "rgba(255,255,255,0.045)",
        "stroke-width": "30",
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
      }),
    )
    svg.appendChild(
      svgEl("path", {
        d: s.paths.center,
        fill: "none",
        stroke: "rgba(255,255,255,0.08)",
        "stroke-width": "29",
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
      }),
    )
    // Faint centre dashes
    svg.appendChild(
      svgEl("path", {
        d: s.paths.center,
        fill: "none",
        stroke: "rgba(255,255,255,0.10)",
        "stroke-width": "1",
        "stroke-dasharray": "2 7",
        "stroke-linecap": "round",
      }),
    )

    // 2. Ideal line — dashed cyan; dimmer when a coloured line is on top (source 290)
    svg.appendChild(
      svgEl("path", {
        d: s.paths.ideal,
        fill: "none",
        stroke: SM_C.ideal,
        "stroke-width": "2",
        "stroke-dasharray": "5 4",
        "stroke-linecap": "round",
        opacity: coloured ? "0.45" : "0.85",
        "vector-effect": "non-scaling-stroke",
      }),
    )

    // 3. Your line (source 293-295)
    if (coloured && pts.you.length) {
      // Per-sample coloured segments
      const segsG = svgEl("g")
      for (let i = 0; i < pts.you.length - 1; i++) {
        const p0 = pts.you[i],
          p1 = pts.you[i + 1]
        const t = (i + 0.5) / (SM_N - 1)
        const col =
          s.mode === "speed"
            ? spdColor(sampleChannel(s.speed, t))
            : inputColor(t, s.points)
        segsG.appendChild(
          svgEl("line", {
            x1: p0[0],
            y1: p0[1],
            x2: p1[0],
            y2: p1[1],
            stroke: col,
            "stroke-width": "3",
            "stroke-linecap": "round",
            "vector-effect": "non-scaling-stroke",
          }),
        )
      }
      svg.appendChild(segsG)
    } else if (coloured) {
      // Fallback if pts not measured yet (shouldn't happen after samplePath)
      svg.appendChild(
        svgEl("path", {
          d: s.paths.you,
          fill: "none",
          stroke: SM_C.coast,
          "stroke-width": "3",
          "stroke-linecap": "round",
          "vector-effect": "non-scaling-stroke",
        }),
      )
    } else {
      // Solid purple (lines mode)
      svg.appendChild(
        svgEl("path", {
          d: s.paths.you,
          fill: "none",
          stroke: SM_C.you,
          "stroke-width": "2.6",
          "stroke-linecap": "round",
          "vector-effect": "non-scaling-stroke",
        }),
      )
    }

    if (pts.you.length) {
      // 4. Direction chevron at entry (source 299-304)
      const mStart = at(pts.you, 0.02)
      const mStartNext = at(pts.you, 0.06)
      const a =
        (Math.atan2(mStartNext[1] - mStart[1], mStartNext[0] - mStart[0]) *
          180) /
        Math.PI
      const chevG = svgEl("g", {
        transform: `translate(${mStart[0]},${mStart[1]}) rotate(${a})`,
        opacity: "0.6",
      })
      chevG.appendChild(
        svgEl("path", {
          d: "M-3 -3 L3 0 L-3 3",
          fill: "none",
          stroke: "#fff",
          "stroke-width": "1.4",
          "stroke-linecap": "round",
          "stroke-linejoin": "round",
          "vector-effect": "non-scaling-stroke",
        }),
      )
      svg.appendChild(chevG)

      // 5. Corner phases (source 307-330)
      for (const c of s.corners) {
        const cornerG = svgEl("g")

        // Helper: draw a phase tick at a given fraction
        const drawPhase = (frac, kind) => {
          const idx = Math.round(frac * (SM_N - 1))
          const p = pts.center[idx]
          const n = normalAt(pts.center, idx)
          const w = kind === "apex" ? 13 : 10.5
          const ax = p[0] - n[0] * w,
            ay = p[1] - n[1] * w
          const bx = p[0] + n[0] * w,
            by = p[1] + n[1] * w
          const op = kind === "apex" ? "0.5" : "0.28"
          return svgEl("line", {
            x1: ax,
            y1: ay,
            x2: bx,
            y2: by,
            stroke: "#fff",
            "stroke-width": kind === "apex" ? "1.4" : "1",
            opacity: op,
            "stroke-linecap": "round",
          })
        }

        cornerG.appendChild(drawPhase(c.start, "start"))
        cornerG.appendChild(drawPhase(c.apex, "apex"))
        cornerG.appendChild(drawPhase(c.exit, "exit"))

        const apexIdx = Math.round(c.apex * (SM_N - 1))
        const ap = pts.center[apexIdx]
        const startIdx = Math.round(c.start * (SM_N - 1))
        const sp = pts.center[startIdx]
        const exitIdx = Math.round(c.exit * (SM_N - 1))
        const xp = pts.center[exitIdx]
        const labelRight = ap[0] > SM_W / 2

        // Apex ring
        cornerG.appendChild(
          svgEl("circle", {
            cx: ap[0],
            cy: ap[1],
            r: "2.6",
            fill: "none",
            stroke: "#fff",
            "stroke-width": "1.4",
          }),
        )

        // Apex label
        cornerG.appendChild(
          svgText(
            ap[0] + (labelRight ? 7 : -7),
            ap[1] + (ap[1] > SM_H / 2 ? 14 : 2),
            c.label,
            {
              "font-size": "8.5",
              "font-family": "JetBrains Mono",
              fill: "rgba(255,255,255,0.62)",
              "text-anchor": labelRight ? "start" : "end",
            },
          ),
        )

        // Turn-in "in" label
        cornerG.appendChild(
          svgText(
            sp[0] + (labelRight ? -3 : 5),
            sp[1] + (sp[1] > SM_H / 2 ? 16 : -6),
            "in",
            {
              "font-size": "6.5",
              "font-family": "JetBrains Mono",
              fill: "rgba(255,255,255,0.4)",
              "text-anchor": labelRight ? "end" : "start",
            },
          ),
        )

        // Track-out "out" label
        cornerG.appendChild(
          svgText(
            xp[0] + (labelRight ? 5 : -3),
            xp[1] + (xp[1] > SM_H / 2 ? 16 : -6),
            "out",
            {
              "font-size": "6.5",
              "font-family": "JetBrains Mono",
              fill: "rgba(255,255,255,0.4)",
              "text-anchor": labelRight ? "start" : "end",
            },
          ),
        )

        svg.appendChild(cornerG)
      }

      // 6. Brake/throttle marker pairs — lines mode (source 333-340)
      if (s.mode === "lines") {
        const markers = [
          {
            key: "thr",
            ideal: at(pts.ideal, s.points.thrIdeal),
            you: at(pts.you, s.points.thrYou),
          },
          {
            key: "brk",
            ideal: at(pts.ideal, s.points.brkIdeal),
            you: at(pts.you, s.points.brkYou),
          },
        ]
        // Resolve note copy per marker (colour/label from key + builder gap/state/fix),
        // then auto-place the callout tags for the markers that have copy.
        const resolved = markers.map((m) => ({
          m,
          note: this.#resolveNote(m.key),
        }))
        const placeItems = resolved
          .filter((r) => r.note.gap)
          .map((r) => ({
            key: r.m.key,
            dot: r.m.you,
            w: calloutWidth(r.note.label, r.note.gap, r.note.state, r.note.fix),
          }))
        const tags = placeCallouts({
          items: placeItems,
          centerPts: pts.center,
          cornerPts: (s.corners || []).map((c) => at(pts.center, c.apex)),
          box: { W: SM_W, H: SM_H, pad: 4 },
        })
        for (const { m, note } of resolved) {
          this._renderPair(
            { ...note, tag: note.gap ? tags[m.key] : null },
            m.ideal,
            m.you,
            s.annot,
          )
        }
      }

      // For coloured modes: solid pedal dots only (source 337-340)
      if (coloured) {
        const mThrYou = at(pts.you, s.points.thrYou)
        const mBrkYou = at(pts.you, s.points.brkYou)
        svg.appendChild(
          svgEl("circle", {
            cx: mThrYou[0],
            cy: mThrYou[1],
            r: "3",
            fill: SM_C.throttle,
            stroke: "#0b1020",
            "stroke-width": "1",
          }),
        )
        svg.appendChild(
          svgEl("circle", {
            cx: mBrkYou[0],
            cy: mBrkYou[1],
            r: "3",
            fill: SM_C.brake,
            stroke: "#0b1020",
            "stroke-width": "1",
          }),
        )
      }
    } else {
      // 9. "reading line…" placeholder (source 352) when pts not available
      const ph = document.createElement("div")
      ph.className =
        "absolute inset-0 flex items-center justify-center font-mono text-[9px] text-white/30"
      ph.textContent = "reading line…"
      this._mapWrap.appendChild(ph)
    }

    // 7. Strip annotation — HTML below SVG (source 376-399)
    if (s.mode === "lines" && s.annot === "strip") {
      const strip = this._buildStrip()
      this.el.appendChild(strip)
    }

    // 8. Scoreboard (source 401-405) — 2-col HTML grid below SVG (and strip)
    const scoreboard = this._buildScoreboard()
    this.el.appendChild(scoreboard)

    // Finally: scrub layer as the SVG's last child (Task 2 draws into it)
    this._scrubLayer = svgEl("g")
    svg.appendChild(this._scrubLayer)
  }

  // Merge a marker's fixed colour/label (from key) with the builder's note copy
  // ({gap,state,fix}). When the marker has no note, gap is undefined → the pair
  // renders geometry only (no callout).
  #resolveNote(key) {
    const n = (this.s.notes && this.s.notes[key]) || {}
    return { ...NOTE_META[key], gap: n.gap, state: n.state, fix: n.fix }
  }

  // Render a brake/throttle marker pair (source renderPair 253-276)
  _renderPair(note, ideal, you, annot) {
    if (!ideal || !you) return
    const ang =
      (Math.atan2(ideal[1] - you[1], ideal[0] - you[0]) * 180) / Math.PI

    const g = svgEl("g")
    const k = 1 / this._zoom

    // Dashed connector — endpoints track the (scaling) you/ideal points so it
    // shows the real gap, but non-scaling-stroke keeps a constant line thickness.
    g.appendChild(
      svgEl("line", {
        x1: you[0],
        y1: you[1],
        x2: ideal[0],
        y2: ideal[1],
        stroke: note.color,
        "stroke-width": "1.2",
        "stroke-dasharray": "2 2",
        opacity: "0.6",
        "vector-effect": "non-scaling-stroke",
      }),
    )

    // Ring + arrowhead at the ideal point — kept constant screen size by
    // counter-scaling the group 1/zoom about that point.
    const idealG = svgEl("g", { transform: scaleAbout(ideal, k) })
    const arrowG = svgEl("g", {
      transform: `translate(${ideal[0]},${ideal[1]}) rotate(${ang})`,
    })
    arrowG.appendChild(
      svgEl("path", {
        d: "M-4 -2.6 L0.6 0 L-4 2.6",
        fill: "none",
        stroke: note.color,
        "stroke-width": "1.3",
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
        opacity: "0.9",
      }),
    )
    idealG.appendChild(arrowG)
    idealG.appendChild(
      svgEl("circle", {
        cx: ideal[0],
        cy: ideal[1],
        r: "3.4",
        fill: "none",
        stroke: note.color,
        "stroke-width": "1.7",
      }),
    )
    g.appendChild(idealG)

    // Solid dot at your point — constant screen size (counter-scaled about it).
    const youG = svgEl("g", { transform: scaleAbout(you, k) })
    youG.appendChild(
      svgEl("circle", {
        cx: you[0],
        cy: you[1],
        r: "3.6",
        fill: note.color,
        stroke: "#0b1020",
        "stroke-width": "1",
      }),
    )
    g.appendChild(youG)

    this._scaleGroups.push({ g: idealG, c: ideal }, { g: youG, c: you })

    // Callout annotation: pinned label near the point (source 268-273). Only
    // when this marker has note copy and an auto-placed tag (skipped markers
    // still draw the dots/ring/arrow above, just no text).
    if (annot === "callout" && note.tag) {
      const calloutG = svgEl("g", { opacity: "1" })
      calloutG.style.transition = "opacity .12s"

      // Leader line from you-dot to callout
      calloutG.appendChild(
        svgEl("line", {
          x1: you[0],
          y1: you[1],
          x2: note.tag.x,
          y2: note.tag.y - 7,
          stroke: note.color,
          "stroke-width": "0.8",
          opacity: "0.4",
        }),
      )

      // Label line 1: THROTTLE / BRAKE header
      calloutG.appendChild(
        svgText(note.tag.x, note.tag.y, note.label.toUpperCase(), {
          "font-size": "7.5",
          "font-family": "JetBrains Mono",
          fill: note.color,
          "text-anchor": note.tag.anchor,
          "letter-spacing": "0.1em",
        }),
      )

      // Label line 2: "you 0.31s late" / "you 9 m early"
      calloutG.appendChild(
        svgText(note.tag.x, note.tag.y + 11, `you ${note.gap} ${note.state}`, {
          "font-size": "9.5",
          "font-family": "JetBrains Mono",
          fill: SM_C.loss,
          "text-anchor": note.tag.anchor,
        }),
      )

      // Label line 3: "→ fix"
      calloutG.appendChild(
        svgText(note.tag.x, note.tag.y + 21, `→ ${note.fix}`, {
          "font-size": "8",
          "font-family": "JetBrains Mono",
          fill: "rgba(255,255,255,0.6)",
          "text-anchor": note.tag.anchor,
        }),
      )

      // Counter-scale the whole callout (leader + text) by 1/zoom about the dot
      // so it keeps a constant screen size and offset while staying connected.
      calloutG.setAttribute("transform", scaleAbout(you, 1 / this._zoom))
      g.appendChild(calloutG)
      this._callouts.push({ g: calloutG })
      this._scaleGroups.push({ g: calloutG, c: you })
    }

    this._svg.appendChild(g)
  }

  // Build the "strip" annotation block (source 376-399)
  _buildStrip() {
    const strip = document.createElement("div")
    strip.className =
      "border-t border-white/[0.06] px-2.5 py-2 flex flex-col gap-2"

    // Header row: "Points vs ideal" + legend key
    const header = document.createElement("div")
    header.className = "flex items-center justify-between"

    const headerLabel = document.createElement("span")
    headerLabel.className =
      "font-mono text-[8px] tracking-[0.16em] uppercase text-white/40"
    headerLabel.textContent = "Points vs ideal"
    header.appendChild(headerLabel)

    const keyWrap = document.createElement("span")
    keyWrap.className =
      "flex items-center gap-2.5 font-mono text-[8px] text-white/45"
    keyWrap.innerHTML =
      '<span class="flex items-center gap-1"><span class="inline-block h-2 w-2 rounded-full ring-[1.4px] ring-white/70"></span>ideal</span>' +
      '<span class="flex items-center gap-1"><span class="inline-block h-2 w-2 rounded-full bg-white/80"></span>you</span>'
    header.appendChild(keyWrap)
    strip.appendChild(header)

    // One row per note (thr + brk)
    for (const n of [this.s.notes.thr, this.s.notes.brk]) {
      const row = document.createElement("div")
      row.className = "flex flex-col gap-0.5"

      const topLine = document.createElement("div")
      topLine.className = "flex items-center gap-2"

      const dot = document.createElement("span")
      dot.className = "h-2 w-2 rounded-full shrink-0"
      dot.style.background = n.color

      const lbl = document.createElement("span")
      lbl.className =
        "font-mono text-[9.5px] uppercase tracking-[0.08em] text-white/75"
      lbl.textContent = n.label

      const delta = document.createElement("span")
      delta.className =
        "ml-auto font-mono text-[10px] tabular-nums whitespace-nowrap"
      delta.style.color = SM_C.loss
      delta.textContent = `you ${n.gap} ${n.state}`

      topLine.appendChild(dot)
      topLine.appendChild(lbl)
      topLine.appendChild(delta)
      row.appendChild(topLine)

      const fixLine = document.createElement("div")
      fixLine.className =
        "pl-4 flex items-center gap-1 font-mono text-[9.5px] text-white/55 whitespace-nowrap"
      fixLine.innerHTML =
        '<svg class="h-2.5 w-2.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>' +
        `<span>${esc(n.fix)}</span>`
      row.appendChild(fixLine)

      strip.appendChild(row)
    }

    return strip
  }

  // Build the scoreboard grid (source 401-405 + SmStat 163-172)
  _buildScoreboard() {
    const grid = document.createElement("div")
    grid.className = "grid grid-cols-2 border-t border-white/[0.06]"

    this.s.stats.forEach((stat, i) => {
      const cell = document.createElement("div")
      if (i < this.s.stats.length - 1)
        cell.className = "border-r border-white/[0.06]"

      const col =
        stat.tone === "loss"
          ? SM_C.loss
          : stat.tone === "gain"
            ? SM_C.gain
            : "#fff"

      // SmStat inner (source 163-172)
      const inner = document.createElement("div")
      inner.className = "px-2.5 py-2"

      const labelEl = document.createElement("div")
      labelEl.className =
        "font-mono text-[8px] tracking-[0.16em] uppercase text-white/40"
      labelEl.textContent = stat.label

      const valueEl = document.createElement("div")
      valueEl.className =
        "mt-1 font-mono text-[13px] tabular-nums leading-none text-white"
      valueEl.textContent = stat.value
      if (stat.unit) {
        const unitEl = document.createElement("span")
        unitEl.className = "text-[8.5px] text-white/45 ml-0.5"
        unitEl.textContent = stat.unit
        valueEl.appendChild(unitEl)
      }

      inner.appendChild(labelEl)
      inner.appendChild(valueEl)

      if (stat.sub) {
        const subEl = document.createElement("div")
        subEl.className = "mt-1 font-mono text-[9px]"
        subEl.style.color = col
        subEl.textContent = stat.sub
        inner.appendChild(subEl)
      }

      cell.appendChild(inner)
      grid.appendChild(cell)
    })

    return grid
  }

  // Client → SVG user-space coords (accounts for the live viewBox).
  _svgPointAt(clientX, clientY) {
    const ctm = this._svg.getScreenCTM()
    if (!ctm) return null
    const p = this._svg.createSVGPoint()
    p.x = clientX
    p.y = clientY
    return p.matrixTransform(ctm.inverse())
  }

  _applyViewBox() {
    this._svg.setAttribute("viewBox", this._vb.join(" "))
  }

  // Keep markers + callout labels a constant screen size as zoom changes (pan
  // doesn't change zoom, so they just ride the scene — no update needed).
  _applyFixedScale() {
    const k = 1 / this._zoom
    this._scaleGroups.forEach(({ g, c }) =>
      g.setAttribute("transform", scaleAbout(c, k)),
    )
  }

  _onMove(e) {
    if (this._dragging) {
      const x =
        this._dragStart.vx -
        (e.clientX - this._dragStart.clientX) * this._dragStart.sx
      const y =
        this._dragStart.vy -
        (e.clientY - this._dragStart.clientY) * this._dragStart.sy
      this._vb = clampViewBox([x, y, this._vb[2], this._vb[3]], {
        W: SM_W,
        H: SM_H,
      })
      this._applyViewBox()
      return
    }
    if (this.s.mode !== "lines") return
    if (!this._pts || !this._pts.you.length) return
    const v = this._svgPointAt(e.clientX, e.clientY)
    if (!v) return
    let best = 0,
      bd = Infinity
    for (let i = 0; i < SM_N; i++) {
      const d =
        (this._pts.you[i][0] - v.x) ** 2 + (this._pts.you[i][1] - v.y) ** 2
      if (d < bd) {
        bd = d
        best = i
      }
    }
    this._hoverI = best
    this._renderScrub()
  }

  _onLeave() {
    this._hoverI = null
    this._renderScrub()
  }

  _onDown(e) {
    const ctm = this._svg.getScreenCTM()
    if (!ctm) return
    this._dragging = true
    this._dragStart = {
      clientX: e.clientX,
      clientY: e.clientY,
      vx: this._vb[0],
      vy: this._vb[1],
      sx: 1 / ctm.a,
      sy: 1 / ctm.d,
    }
    this._svg.style.cursor = "grabbing"
  }

  _onUp() {
    if (!this._dragging) return
    this._dragging = false
    if (this._svg) this._svg.style.cursor = ""
  }

  _onWheel(e) {
    e.preventDefault()
    const anchor = this._svgPointAt(e.clientX, e.clientY)
    if (!anchor) return
    this._zoom = clampZoom(this._zoom * (e.deltaY < 0 ? 1.1 : 1 / 1.1))
    this._vb = viewBoxForZoom(this._vb, this._zoom, anchor, {
      W: SM_W,
      H: SM_H,
    })
    this._applyViewBox()
    this._applyFixedScale()
  }

  _onDblClick() {
    this._zoom = 1
    this._vb = [0, 0, SM_W, SM_H]
    this._applyViewBox()
    this._applyFixedScale()
  }

  _renderScrub() {
    if (this.s.mode !== "lines") return
    const pts = this._pts
    const ready = pts && pts.you.length
    const hov =
      ready && this._hoverI != null
        ? (() => {
            const i = this._hoverI,
              t = i / (SM_N - 1)
            return {
              you: pts.you[i],
              ideal: pts.ideal[i],
              thrYou: sampleChannel(this.s.channels.thrYou, t),
              thrRef: sampleChannel(this.s.channels.thrRef, t),
              brkYou: sampleChannel(this.s.channels.brkYou, t),
              brkRef: sampleChannel(this.s.channels.brkRef, t),
              ctx: contextLabel(t, this.s.corners),
            }
          })()
        : null

    // 1) scrub layer: tie-line + dots on both lines (or empty)
    this._scrubLayer.replaceChildren()
    if (hov) {
      this._scrubLayer.append(
        svgEl("line", {
          x1: hov.you[0],
          y1: hov.you[1],
          x2: hov.ideal[0],
          y2: hov.ideal[1],
          stroke: "rgba(255,255,255,0.28)",
          "stroke-width": 1,
        }),
        svgEl("circle", {
          cx: hov.ideal[0],
          cy: hov.ideal[1],
          r: 5.5,
          fill: SM_C.ideal,
          opacity: 0.18,
        }),
        svgEl("circle", {
          cx: hov.ideal[0],
          cy: hov.ideal[1],
          r: 3.2,
          fill: SM_C.ideal,
          stroke: "#0b1020",
          "stroke-width": 1,
        }),
        svgEl("circle", {
          cx: hov.you[0],
          cy: hov.you[1],
          r: 6,
          fill: SM_C.you,
          opacity: 0.2,
        }),
        svgEl("circle", {
          cx: hov.you[0],
          cy: hov.you[1],
          r: 3.4,
          fill: SM_C.you,
          stroke: "#fff",
          "stroke-width": 1,
        }),
      )
    }

    // 2) callout fade
    this._callouts.forEach(({ g }) => {
      g.style.opacity = hov ? "0.22" : "1"
    })

    // 3) HUD + idle hint
    if (hov) {
      this._hud.innerHTML = `
        <div class="rounded-md px-2 py-1.5 bg-black/65 ring-1 ring-white/10" style="backdrop-filter:blur(2px)">
          <div class="flex items-center justify-between gap-3 mb-1">
            <span class="font-mono text-[7px] uppercase tracking-[0.12em] text-white/50 whitespace-nowrap">${esc(hov.ctx)}</span>
            <span class="font-mono text-[7px] text-white/30">you·ref</span>
          </div>
          <div class="flex flex-col gap-1">
            ${this._inputRow("THR", SM_C.throttle, hov.thrYou, hov.thrRef)}
            ${this._inputRow("BRK", SM_C.brake, hov.brkYou, hov.brkRef)}
          </div>
        </div>`
      this._hint.style.display = "none"
    } else {
      this._hud.innerHTML = ""
      this._hint.style.display = ready ? "" : "none"
    }
  }

  // One HUD input row: a you-bar over a dimmed ref-bar + the two %s (source SmInputRow 175-191).
  _inputRow(label, color, you, refV) {
    const bar = (v, dim) => `
      <div class="rounded-full bg-white/[0.13] overflow-hidden" style="height:4px;width:50px">
        <div class="h-full rounded-full" style="width:${Math.round(v * 100)}%;background:${color};opacity:${dim ? 0.5 : 1};transition:width .08s linear"></div>
      </div>`
    return `
      <div class="flex items-center gap-1.5">
        <span class="font-mono text-[7.5px] w-5 shrink-0" style="color:${color}">${esc(label)}</span>
        <div class="flex flex-col gap-[3px]">${bar(you, false)}${bar(refV, true)}</div>
        <div class="flex flex-col gap-[3px] font-mono text-[8px] tabular-nums leading-none w-5 text-right">
          <span style="color:${color}">${Math.round(you * 100)}</span>
          <span class="text-white/45">${Math.round(refV * 100)}</span>
        </div>
      </div>`
  }
}

function mergeSection(section) {
  const d = DEMO_SECTION
  const s = section || {}
  return {
    title: s.title ?? d.title,
    mode: s.mode ?? d.mode,
    annot: s.annot ?? d.annot,
    paths: { ...d.paths, ...(s.paths || {}) },
    points: { ...d.points, ...(s.points || {}) },
    apex: { ...d.apex, ...(s.apex || {}) },
    // Use the builder's notes as-is (it always sets notes, possibly {} or a
    // single marker) so demo copy never leaks onto real sections; fall back to
    // demo notes only for the standalone demo (no section.notes at all).
    notes: s.notes ?? d.notes,
    corners: s.corners ?? d.corners,
    channels: { ...d.channels, ...(s.channels || {}) },
    speed: s.speed ?? d.speed,
    stats: s.stats ?? d.stats,
  }
}
